/**
 * Map v2 Normalized Deals to the canonical Deal type for UI preview
 * Used by FlyerDiagnosticsPanelV2 to render DealGiftCard previews
 */

import type { Deal, DealCategory } from "@/lib/types/deals";
import type { NormalizedDeal } from "./types";

export interface NormalizedDealRunContext {
  merchant_id: string;
  merchant_name: string;
  landing_url: string;
  region: string;
}

// Known loyalty program mappings for common Canadian retailers
// This allows us to infer program details from merchant/reward names
const LOYALTY_PROGRAM_LOOKUP: Record<string, {
  programId: string;
  programName: string;
  conversionRate: number;
}> = {
  "scene+": { programId: "scene-plus", programName: "Scene+", conversionRate: 0.01 },
  "scene plus": { programId: "scene-plus", programName: "Scene+", conversionRate: 0.01 },
  "scene": { programId: "scene-plus", programName: "Scene+", conversionRate: 0.01 },
  "pc optimum": { programId: "pc-optimum", programName: "PC Optimum", conversionRate: 0.001 },
  "optimum": { programId: "pc-optimum", programName: "PC Optimum", conversionRate: 0.001 },
  "air miles": { programId: "air-miles", programName: "AIR MILES", conversionRate: 0.105 },
  "airmiles": { programId: "air-miles", programName: "AIR MILES", conversionRate: 0.105 },
  "triangle rewards": { programId: "triangle-rewards", programName: "Triangle Rewards", conversionRate: 0.01 },
  "triangle": { programId: "triangle-rewards", programName: "Triangle Rewards", conversionRate: 0.01 },
  "ct money": { programId: "triangle-rewards", programName: "CT Money", conversionRate: 0.01 },
};

// Merchants known to use specific loyalty programs
const MERCHANT_LOYALTY_DEFAULTS: Record<string, string> = {
  "freshco": "scene+",
  "sobeys": "scene+",
  "safeway": "scene+",
  "foodland": "scene+",
  "iga": "scene+",
  "thrifty foods": "scene+",
  "farm boy": "scene+",
  "loblaws": "pc optimum",
  "no frills": "pc optimum",
  "real canadian superstore": "pc optimum",
  "superstore": "pc optimum",
  "shoppers drug mart": "pc optimum",
  "canadian tire": "triangle rewards",
  "sport chek": "triangle rewards",
  "mark's": "triangle rewards",
  "atmosphere": "triangle rewards",
};

/**
 * Detect loyalty program from reward text, notes, or merchant context
 */
function detectLoyaltyProgram(
  normalizedDeal: NormalizedDeal,
  merchantName: string
): { programId: string; programName: string; conversionRate: number } | null {
  // Check AI notes for program mentions
  const aiNotes = normalizedDeal.debug?.ai_notes?.join(" ").toLowerCase() || "";
  const rewardMerchant = normalizedDeal.reward?.merchant?.toLowerCase() || "";
  const merchantLower = merchantName.toLowerCase();
  
  // First, try to match from AI notes or reward merchant
  for (const [key, program] of Object.entries(LOYALTY_PROGRAM_LOOKUP)) {
    if (aiNotes.includes(key) || rewardMerchant.includes(key)) {
      return program;
    }
  }
  
  // Fall back to merchant-based defaults
  for (const [merchant, programKey] of Object.entries(MERCHANT_LOYALTY_DEFAULTS)) {
    if (merchantLower.includes(merchant)) {
      return LOYALTY_PROGRAM_LOOKUP[programKey] || null;
    }
  }
  
  return null;
}

/**
 * Map a v2 NormalizedDeal to the canonical Deal type for UI rendering
 */
export function mapNormalizedDealToUIDeal(
  normalizedDeal: NormalizedDeal,
  runContext: NormalizedDealRunContext
): Deal {
  // Map deal_type string to DealCategory
  const categoryMap: Record<string, DealCategory> = {
    BONUS_GIFT_CARD: "BONUS",
    POINTS_BACK: "POINTS",
    DISCOUNT: "DISCOUNT",
    IN_KIND: "IN_KIND",
    UNCLASSIFIED: "UNCLASSIFIED",
  };
  
  const dealCategory = categoryMap[normalizedDeal.deal_type] || "UNCLASSIFIED";
  
  // Map confidence (0-1 float) to display level
  const confidenceValue = normalizedDeal.confidence ?? 0;
  const confidence: "High" | "Medium" | "Low" = 
    confidenceValue >= 0.8 ? "High" :
    confidenceValue >= 0.5 ? "Medium" : "Low";

  // Extract purchase and reward info
  const purchaseBrand = normalizedDeal.purchase?.brand || null;
  const purchaseAmount = normalizedDeal.purchase?.amount || null;
  const rewardAmount = normalizedDeal.reward?.amount || null;
  const rewardType = normalizedDeal.reward?.type || null;
  const rewardMerchant = normalizedDeal.reward?.merchant || normalizedDeal.merchant || runContext.merchant_name;
  
  // Detect loyalty program for POINTS deals
  const loyaltyProgram = dealCategory === "POINTS" 
    ? detectLoyaltyProgram(normalizedDeal, runContext.merchant_name)
    : null;
  
  // Calculate computed values
  let computedValueCad: number | null = null;
  let effectivePercentBack: number | null = null;
  let discountPercent: number | null = null;
  let pointsAmount: number | null = null;
  let bonusValueCad: number | null = null;
  let pointsProgramName: string | null = null;
  let pointsToCurrencyRate: number | null = null;
  
  if (dealCategory === "BONUS" && rewardAmount) {
    bonusValueCad = rewardAmount;
    computedValueCad = rewardAmount;
    if (purchaseAmount && purchaseAmount > 0) {
      effectivePercentBack = (rewardAmount / purchaseAmount) * 100;
    }
  } else if (dealCategory === "DISCOUNT" && rewardAmount) {
    // For discounts, reward amount might be percentage
    discountPercent = rewardAmount;
    if (purchaseAmount && purchaseAmount > 0) {
      computedValueCad = purchaseAmount * (rewardAmount / 100);
      effectivePercentBack = rewardAmount;
    }
  } else if (dealCategory === "POINTS" && rewardAmount) {
    pointsAmount = rewardAmount;
    
    // Use detected loyalty program conversion rate
    if (loyaltyProgram) {
      pointsProgramName = loyaltyProgram.programName;
      pointsToCurrencyRate = loyaltyProgram.conversionRate;
      computedValueCad = rewardAmount * loyaltyProgram.conversionRate;
      if (purchaseAmount && purchaseAmount > 0) {
        effectivePercentBack = (computedValueCad / purchaseAmount) * 100;
      }
    } else {
      // Default fallback if no program detected
      pointsProgramName = "points";
      pointsToCurrencyRate = null;
      computedValueCad = null;
    }
  }

  // Generate headline mechanic
  let headlineMechanic = "";
  const brandPart = purchaseBrand ? ` ${purchaseBrand}` : "";
  
  if (dealCategory === "POINTS" && pointsAmount && purchaseAmount) {
    const programLabel = pointsProgramName || "points";
    headlineMechanic = `Spend $${purchaseAmount} on${brandPart} gift cards and get ${pointsAmount.toLocaleString()} ${programLabel}`;
  } else if (dealCategory === "POINTS" && pointsAmount) {
    const programLabel = pointsProgramName || "points";
    headlineMechanic = `Get ${pointsAmount.toLocaleString()} ${programLabel} on${brandPart} gift cards`;
  } else if (dealCategory === "BONUS" && bonusValueCad && purchaseAmount) {
    headlineMechanic = `Spend $${purchaseAmount} on${brandPart} gift cards, get $${bonusValueCad} bonus`;
  } else if (dealCategory === "BONUS" && bonusValueCad) {
    headlineMechanic = `Get $${bonusValueCad} bonus on${brandPart} gift cards`;
  } else if (dealCategory === "DISCOUNT" && discountPercent && purchaseAmount) {
    headlineMechanic = `Spend $${purchaseAmount}, get ${discountPercent}% off${brandPart} gift cards`;
  } else if (dealCategory === "DISCOUNT" && discountPercent) {
    headlineMechanic = `${discountPercent}% off${brandPart} gift cards`;
  } else {
    headlineMechanic = "Gift card offer";
  }

  // For flyer deals:
  // - Header should show the gift card brand you're BUYING (purchase_brand from AI)
  // - Ribbon should show WHERE you get the bonus FROM (merchant from AI, e.g., Walmart)
  // This ensures "Spend $50 on Petro-Canada, get $10 Walmart bonus" displays correctly
  // Use AI-detected values first, fall back to run context
  const headerMerchant = purchaseBrand || normalizedDeal.merchant || runContext.merchant_name;
  const ribbonBonusMerchant = normalizedDeal.merchant || rewardMerchant || runContext.merchant_name;

  return {
    // Core identification - use PURCHASE BRAND as header merchant for flyer deals
    merchant: headerMerchant,
    
    // Deal Type System
    deal_category: dealCategory,
    headline_mechanic: headlineMechanic,
    
    // Purchase gift card details
    purchase_gift_card_brand: purchaseBrand,
    purchase_brand_logo_url: null,
    
    // Bonus fields - flyer merchant provides the bonus
    bonus_merchant: ribbonBonusMerchant,
    bonus_merchant_logo_url: null,
    bonus_amount: bonusValueCad,
    spend_amount: purchaseAmount,
    qualifying_spend_cad: purchaseAmount,
    bonus_value_cad: bonusValueCad,
    
    // Legacy field
    gift_card_brand: purchaseBrand,
    bonus_or_discount_amount: bonusValueCad ?? discountPercent,
    
    deal_type: (normalizedDeal.deal_type?.toLowerCase() || "unclassified") as Deal["deal_type"],
    
    // Discount fields
    discount_percent: discountPercent,
    
    // Points fields - NOW POPULATED WITH PROGRAM INFO
    points_amount: pointsAmount,
    points_program_name: pointsProgramName,
    points_to_currency_rate: pointsToCurrencyRate,
    
    // Computed values
    computed_value_cad: computedValueCad,
    effective_percent_back: effectivePercentBack,
    
    // Range/percent deal fields
    spend_range_min: null,
    spend_range_max: null,
    discount_tiers: null,
    deal_pricing_type: null,
    
    // Validity dates
    start_date: normalizedDeal.validity?.start_date || null,
    end_date: normalizedDeal.validity?.end_date || null,
    bonus_valid_from: null,
    bonus_valid_until: null,
    
    // Portal/claim details
    requires_physical_card: false,
    claim_url: null,
    redeem_url: null,
    claim_deadline: null,
    campaign_name: null,
    
    // Fine print
    fine_print: normalizedDeal.validity?.raw_text || "",
    
    // Source type and provenance
    source_type: "flyer",
    source_url: runContext.landing_url,
    flyer_page_number: normalizedDeal.page_no,
    
    // Location/channel
    channel: "in-store",
    region: runContext.region,
    
    // Proof and verification
    proof_urls: [],
    confidence,
    confidence_reason: `AI detection (score: ${(confidenceValue * 100).toFixed(0)}%)`,
    raw_source_notes: normalizedDeal.debug?.ai_notes?.join("; ") || "",
    
    // Evidence
    evidence_snippet: normalizedDeal.debug?.ai_notes?.[0] || "",
    
    // Active status based on dates
    is_active: true,
  };
}
